// PO1-2057: UI pública pra transportadora preencher valores de Cotação Spot.
// Acessada via link único com JWT no path: /public/cotacao-spot/:token
//
// Cores via tokens --everlog-* / --theme-* (vivem em :root e
// body.white-content) — funciona em ambos os temas sem forçar nenhum.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import ApiHubBackend from "../../../services/ApiHubBackend";
import autopilotLogo from "../../../assets/img/autopilot-logo.png";

const SHELL_STYLE = {
  minHeight: "100vh",
  background: "var(--everlog-card-hero-bg)",
  padding: "32px 16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
};

const BRAND_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 24,
};

const BRAND_LEFT_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

// Pill do contador — base + variantes por nível de urgência. Cor de fundo
// é translúcida via color-mix() em cima do token semântico, garantindo
// boa leitura em ambos os temas.
const EXPIRATION_PILL_BASE = {
  borderRadius: 999,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  whiteSpace: "nowrap",
  letterSpacing: 0.2,
};

function pillStyleByLevel(level) {
  switch (level) {
    case "danger":
      return {
        ...EXPIRATION_PILL_BASE,
        color: "var(--everlog-status-danger)",
        background:
          "color-mix(in srgb, var(--everlog-status-danger) 14%, transparent)",
        border: "1px solid var(--everlog-status-danger)",
        boxShadow:
          "0 0 0 4px color-mix(in srgb, var(--everlog-status-danger) 12%, transparent)",
      };
    case "warning":
      return {
        ...EXPIRATION_PILL_BASE,
        color: "var(--everlog-status-warning)",
        background:
          "color-mix(in srgb, var(--everlog-status-warning) 14%, transparent)",
        border: "1px solid var(--everlog-status-warning)",
      };
    case "success":
    default:
      return {
        ...EXPIRATION_PILL_BASE,
        color: "var(--everlog-status-success)",
        background:
          "color-mix(in srgb, var(--everlog-status-success) 14%, transparent)",
        border: "1px solid var(--everlog-status-success)",
      };
  }
}

// Robozinho do Autopilot + texto "Everlog" — pattern usado na tela de Login.
// Cor do texto via token --everlog-card-title (adapta tema claro/escuro).
const BRAND_LOGO_STYLE = {
  width: 44,
  height: 44,
  objectFit: "contain",
};

const BRAND_TEXT_STYLE = {
  color: "var(--everlog-card-title)",
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: 0.3,
};

const FOOTER_STYLE = {
  marginTop: 32,
  textAlign: "center",
  color: "var(--everlog-card-text-soft)",
  fontSize: 12,
  letterSpacing: 0.3,
};

const FOOTER_LINK_STYLE = {
  color: "var(--theme-accent)",
  textDecoration: "none",
  fontWeight: 500,
};

const CARD_STYLE = {
  borderRadius: 12,
  boxShadow: "var(--everlog-card-elevated-shadow)",
  border: "1px solid var(--everlog-card-border-soft)",
  background: "var(--everlog-card-surface-elevated)",
  overflow: "hidden",
};

const HEADER_STYLE = {
  background: "linear-gradient(135deg, var(--theme-accent-surface, var(--theme-accent)), var(--theme-accent-surface-strong, var(--theme-accent-strong)))",
  color: "var(--everlog-on-accent-text)",
  padding: "20px 28px",
};

const ROUTE_TABLE_STYLE = {
  background: "var(--everlog-card-surface-section)",
  border: "1px solid var(--everlog-card-border-soft)",
  borderRadius: 8,
  padding: "12px 16px",
  marginBottom: 24,
};

const VEHICLE_ROW_STYLE = {
  borderBottom: "1px solid var(--everlog-card-divider)",
  padding: "16px 0",
};

const ERROR_MAP = {
  invalid_token:
    "Link inválido. Verifique se você copiou a URL completa do email.",
  expired:
    "Esse link expirou. Solicite um novo no portal Everlog.",
  wrong_scope:
    "Esse link não é válido pra este fluxo.",
  context_not_found:
    "Não encontramos os dados desta cotação. O link pode ter sido invalidado.",
  tabelas_not_found:
    "As tabelas referenciadas no link não estão mais ativas.",
};

function formatBRL(value) {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseBRL(masked) {
  if (!masked) return null;
  const digits = String(masked).replace(/\D/g, "");
  if (!digits) return null;
  return Number(digits) / 100;
}

// Countdown legível: "expira em 6 dias 22h", "5h 32min", "47min", "expirado".
// Retorna { texto, level } onde level = "success" | "warning" | "danger".
//   success: > 3 dias  — verde, link bem fresco
//   warning: 1–3 dias  — amarelo, atenção
//   danger:  < 24h ou expirado — vermelho, urgente
function formatTempoRestante(expiraEmIso) {
  if (!expiraEmIso) return null;
  const expira = new Date(expiraEmIso).getTime();
  if (!Number.isFinite(expira)) return null;
  const diff = expira - Date.now();
  if (diff <= 0) return { texto: "Link expirado", level: "danger" };
  const segundosTotais = Math.floor(diff / 1000);
  const dias = Math.floor(segundosTotais / 86400);
  const horas = Math.floor((segundosTotais % 86400) / 3600);
  const minutos = Math.floor((segundosTotais % 3600) / 60);
  let texto;
  if (dias > 0) {
    texto = `Expira em ${dias} ${dias === 1 ? "dia" : "dias"} ${horas}h`;
  } else if (horas > 0) {
    texto = `Expira em ${horas}h ${minutos}min`;
  } else {
    texto = `Expira em ${minutos}min`;
  }
  let level = "success";
  if (diff < 24 * 3600 * 1000) level = "danger";
  else if (diff < 3 * 24 * 3600 * 1000) level = "warning";
  return { texto, level };
}

// Shell envolvendo cada branch da página com brand header + rodapé Everlog.
// Definido fora do componente principal pra evitar re-mount entre renders.
// Recebe expiration opcional ({ texto, warning }) pra renderizar pill no header.
const Shell = ({ children, expiration }) => (
  <div style={SHELL_STYLE}>
    <div style={{ width: "100%", maxWidth: 720 }}>
      <div style={BRAND_HEADER_STYLE}>
        <div style={BRAND_LEFT_STYLE}>
          <img
            src={autopilotLogo}
            alt=""
            style={BRAND_LOGO_STYLE}
            aria-hidden="true"
          />
          <span style={BRAND_TEXT_STYLE}>Everlog</span>
        </div>
        {expiration && (
          <span
            style={pillStyleByLevel(expiration.level)}
            title="Validade do link"
          >
            <span aria-hidden="true">⏱</span> {expiration.texto}
          </span>
        )}
      </div>
      {children}
      <div style={FOOTER_STYLE}>
        Esta solicitação foi enviada via plataforma{" "}
        <a
          href="https://www.everlogbrasil.com.br"
          target="_blank"
          rel="noopener noreferrer"
          style={FOOTER_LINK_STYLE}
        >
          Everlog
        </a>
        . Em caso de dúvida, entre em contato com o embarcador.
      </div>
    </div>
  </div>
);

const CotacaoSpot = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  const [contexto, setContexto] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  // Tick a cada minuto pra atualizar o countdown — não precisa por segundo.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Carrega contexto inicial
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorCode(null);
    // ApiHubBackend.baseURL já termina em "/api"
    ApiHubBackend.get(`/cotacao-spot/${token}`)
      .then((response) => {
        if (cancelled) return;
        setContexto(response.data);
        const initial = {};
        for (const v of response.data.veiculos || []) {
          initial[v.idFreteTabela] = { valorMascarado: "", naoAtende: false };
        }
        setRespostas(initial);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.data?.error || "internal_error";
        setErrorCode(code);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const updateValor = useCallback((idFreteTabela, raw) => {
    setRespostas((prev) => {
      const number = parseBRL(raw);
      const masked = number == null ? "" : formatBRL(number);
      return {
        ...prev,
        [idFreteTabela]: {
          ...(prev[idFreteTabela] || {}),
          valorMascarado: masked,
          naoAtende: false,
        },
      };
    });
  }, []);

  const toggleNaoAtende = useCallback((idFreteTabela, checked) => {
    setRespostas((prev) => ({
      ...prev,
      [idFreteTabela]: {
        ...(prev[idFreteTabela] || {}),
        naoAtende: checked,
        valorMascarado: checked ? "" : prev[idFreteTabela]?.valorMascarado || "",
      },
    }));
  }, []);

  const podeEnviar = useMemo(() => {
    if (!contexto) return false;
    return contexto.veiculos.some((v) => {
      const r = respostas[v.idFreteTabela];
      if (!r) return false;
      if (r.naoAtende) return true;
      const num = parseBRL(r.valorMascarado);
      return num !== null && num > 0;
    });
  }, [contexto, respostas]);

  const handleSubmit = useCallback(async () => {
    if (!contexto || !podeEnviar) return;
    setSubmitting(true);
    try {
      const payload = {
        respostas: contexto.veiculos
          .map((v) => {
            const r = respostas[v.idFreteTabela] || {};
            if (r.naoAtende) {
              return { idFreteTabela: v.idFreteTabela, naoAtende: true };
            }
            const num = parseBRL(r.valorMascarado);
            if (num != null && num > 0) {
              return { idFreteTabela: v.idFreteTabela, custoCotado: num };
            }
            return null;
          })
          .filter(Boolean),
      };
      const response = await ApiHubBackend.post(
        `/cotacao-spot/${token}`,
        payload
      );
      setResultado(response.data);
    } catch (err) {
      const code = err?.response?.data?.error || "internal_error";
      setErrorCode(code);
    } finally {
      setSubmitting(false);
    }
  }, [contexto, respostas, token, podeEnviar]);

  // ===== Render branches =====

  if (loading) {
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <CardBody
            style={{
              textAlign: "center",
              padding: 48,
              color: "var(--everlog-card-text)",
            }}
          >
            <Spinner color="primary" />
            <p
              style={{
                marginTop: 16,
                color: "var(--everlog-card-muted)",
              }}
            >
              Carregando cotação…
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  if (errorCode && !contexto) {
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <CardBody style={{ padding: 32 }}>
            <h4
              style={{
                color: "var(--everlog-status-danger)",
                marginBottom: 12,
              }}
            >
              Não foi possível abrir essa cotação
            </h4>
            <p style={{ color: "var(--everlog-card-text)" }}>
              {ERROR_MAP[errorCode] ||
                "Algo deu errado ao carregar essa cotação. Tente novamente em instantes."}
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  if (resultado) {
    const totalSucesso = resultado.inseridos?.length || 0;
    const totalConflitos = resultado.conflitos?.length || 0;
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <div style={HEADER_STYLE}>
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              Tabela atualizada
            </h3>
          </div>
          <CardBody style={{ padding: 32 }}>
            {totalSucesso > 0 && (
              <p style={{ color: "var(--everlog-card-text)" }}>
                <strong>{totalSucesso}</strong>{" "}
                {totalSucesso === 1 ? "valor cadastrado" : "valores cadastrados"}{" "}
                com sucesso. Próximas simulações dessa rota já vão retornar
                automaticamente.
              </p>
            )}
            {totalConflitos > 0 && (
              <p style={{ color: "var(--everlog-card-muted)" }}>
                {totalConflitos === 1
                  ? "1 rota já havia sido cadastrada por uma resposta anterior — mantida sem alteração."
                  : `${totalConflitos} rotas já haviam sido cadastradas por respostas anteriores — mantidas sem alteração.`}
              </p>
            )}
            <p
              style={{
                color: "var(--everlog-card-text-soft)",
                fontSize: 14,
                marginTop: 24,
              }}
            >
              Obrigado pela colaboração!
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  const { embarcador, transportadora, origem, destino, veiculos, solicitante, expiraEm } =
    contexto;
  const countdown = formatTempoRestante(expiraEm);

  return (
    <Shell expiration={countdown}>
      <Card style={CARD_STYLE}>
        <div style={HEADER_STYLE}>
          <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 1 }}>
            ATUALIZAÇÃO DE TABELA · EMBARCADOR{" "}
            <strong>{embarcador.nome.toUpperCase()}</strong>
          </div>
          <h3 style={{ margin: "4px 0 0", fontWeight: 600 }}>
            {transportadora.nome}
          </h3>
        </div>

        <CardBody style={{ padding: "28px 32px" }}>
          <p
            style={{
              color: "var(--everlog-card-text)",
              marginBottom: 16,
            }}
          >
            A tabela vigente entre <strong>{embarcador.nome}</strong> e{" "}
            <strong>{transportadora.nome}</strong> não cobre essa rota. Informe
            o valor por veículo abaixo para que a rota fique disponível na
            simulação automática.
          </p>

          {solicitante && solicitante.email && (
            <div
              style={{
                background: "var(--everlog-card-surface-pill)",
                border: "1px solid var(--everlog-card-border-soft)",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 20,
                fontSize: 13,
                color: "var(--everlog-card-text-soft)",
              }}
            >
              <span style={{ color: "var(--everlog-card-muted)" }}>
                Solicitado por:
              </span>{" "}
              <strong style={{ color: "var(--everlog-card-text)" }}>
                {solicitante.email}
              </strong>
            </div>
          )}

          <div style={ROUTE_TABLE_STYLE}>
            <Row>
              <Col xs={6}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--everlog-card-muted)",
                  }}
                >
                  Origem
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    color: "var(--everlog-card-title)",
                  }}
                >
                  {origem.cidade}/{origem.uf}
                </div>
              </Col>
              <Col xs={6}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--everlog-card-muted)",
                  }}
                >
                  Destino
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    color: "var(--everlog-card-title)",
                  }}
                >
                  {destino.cidade}/{destino.uf}
                </div>
              </Col>
            </Row>
          </div>

          <h5
            style={{
              marginBottom: 12,
              color: "var(--everlog-card-title)",
            }}
          >
            Veículos do contrato vigente
          </h5>

          {veiculos.map((v) => {
            const r = respostas[v.idFreteTabela] || {};
            return (
              <div key={v.idFreteTabela} style={VEHICLE_ROW_STYLE}>
                <Row className="align-items-center">
                  <Col xs={12} md={5}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "var(--everlog-card-title)",
                      }}
                    >
                      {v.nome}
                    </div>
                  </Col>
                  <Col xs={8} md={4}>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--everlog-card-muted)",
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      >
                        R$
                      </span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="0,00"
                        disabled={r.naoAtende}
                        value={r.valorMascarado || ""}
                        onChange={(e) =>
                          updateValor(v.idFreteTabela, e.target.value)
                        }
                        style={{ paddingLeft: 36 }}
                      />
                    </div>
                  </Col>
                  <Col xs={4} md={3}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        marginBottom: 0,
                        fontSize: 13,
                        color: "var(--everlog-card-text)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!r.naoAtende}
                        onChange={(e) =>
                          toggleNaoAtende(v.idFreteTabela, e.target.checked)
                        }
                        style={{ marginRight: 8 }}
                      />
                      Não atendo
                    </label>
                  </Col>
                </Row>
              </div>
            );
          })}

          {errorCode && (
            <div
              style={{
                color: "var(--everlog-status-danger)",
                marginTop: 16,
                fontSize: 14,
              }}
            >
              {ERROR_MAP[errorCode] ||
                "Algo deu errado. Tente enviar novamente."}
            </div>
          )}

          <Button
            color="primary"
            block
            disabled={!podeEnviar || submitting}
            onClick={handleSubmit}
            style={{
              marginTop: 24,
              padding: "12px",
              fontWeight: 600,
            }}
          >
            {submitting ? <Spinner size="sm" /> : "Enviar valores"}
          </Button>
        </CardBody>
      </Card>
    </Shell>
  );
};

export default CotacaoSpot;
